/*
 * Copyright (c) 2026.
 * Spin in het Web B.V. Apeldoorn.
 * Deze software mag alleen gebruikt / bekeken / gewijzigd worden volgens de voorwaarden die zijn vastgelegd in een bijbehorende schriftelijke overeenkomst met Spin in het Web B.V.
 */

/**
 * Auteur: Jelmer Jellema - Spin in het Web B.V
 * Gegenereerd met Claude Code (AI), 09-07-2026
 *
 * Representatie van het geladen model-document, met een klein aantal permanente services als
 * omgeving (`GlobalModelcanvasService` voor de graaf, `TranslateService` voor standaardnamen). Dit is
 * de eigenaar van de niet-graaf-state die voorheen als losse properties op de AngularJS
 * `model`-singleton stond. De graaf zelf blijft in Cytoscape (`cy`) wonen; deze class biedt de query-
 * en mutatie-API daaroverheen.
 *
 * Sinds golf c2 (vervolg) een volwaardige `@Injectable({providedIn:'root'})`: één instantie voor de
 * hele sessie (zie `ontwerp-modelclass.md`, kernbesluit 3). Tot golf 4f meldde hij elke mutatie ook
 * aan AngularJS, via een callback die de migratie-interface registreerde; nu werkt alles via de
 * signals.
 *
 * Golf a (Stap 4, zie `migratieplan.md`) legde het fundament: de document-scalars leven hier als
 * signals, zodat de componenten er reactief op zijn. De publieke accessor-namen (incl. snake_case,
 * zoals `fastest_path` en `effectief_modelleerniveau`) komen uit het AngularJS-model: tot golf 4f
 * delegeerde de AngularJS `model`-service zijn gelijknamige properties hiernaartoe, en toen was het
 * één gedeeld contract tussen beide werelden.
 */
import {Injectable, signal, type Signal, WritableSignal} from '@angular/core';
import type {CollectionReturnValue, Core, EdgeSingular, ElementDefinition, NodeCollection, NodeSingular, Position} from 'cytoscape';
import {TranslateService} from '@ngx-translate/core';
import {elements} from 'dynalearn-common/lib/elements';
import {specialenaam} from '../../lib/specialenaam';
import {GlobalModelcanvasService} from './global-modelcanvas.service';
import {SamenwerkingService} from '../../services/samenwerking.service';
import {CondtagService} from '../../services/condtag.service';
import {ModelSerialisatie, type ModelDocument, type ModelElementData} from './model-serialisatie';
import {Melding} from '../../lib/melding';
import type {NormmatchMapping} from 'dynalearn-common/lib/normmatch.types';
import type {Tekenaanleiding} from './opslag.service';

/**
 * De notatieversie waarin wij een normmodel opschrijven. Oudere documenten worden bij het laden
 * bijgewerkt; zie `werkNormnotatieBij`.
 */
const NORM_NOTATIEVERSIE = 4;

/**
 * Scratch-sleutel op een node die deze client zelf zojuist geplaatst heeft (en daarom selecteert). Het
 * naamveld (`dl-node-label`) opent dan meteen, zonder nodemenu, en haalt de markering weer weg. Scratch
 * gaat niet mee in het opslagformaat en niet over de samenwerklijn.
 */
export const NET_GEPLAATST = 'dlNetGeplaatst';

/**
 * Ruwe elementdefinitie voor `DlModel.addCyNode`, vlak vóór het daadwerkelijke `cy.add(...)`.
 * Wordt door de methode zelf verder aangevuld (classes, scratch.definition, mln, childIds, id, …);
 * `condtag`/`condstate` moeten door de caller al gezet zijn (of bewust weggelaten) — DlModel beslist
 * niet over de actieve condtag, dat is sessie-state.
 */
export interface NieuweNodeData {
    group?: string;
    classes?: string;
    selected?: boolean;
    scratch?: Record<string, unknown>;
    data: {
        type: string;
        id?: string;
        nodename?: string;
        [key: string]: unknown;
    };
    position: Position;
}

/**
 * Het normmodel-document: het als norm geladen model plus de daaruit afgeleide verwachtingstellingen
 * (`teller` per elementtype, `namen` per naam) die de normbalk projecteert. Wordt bij het laden door
 * de `OpslagService` neergezet (uit `meta.norm` van het document, of opgebouwd met
 * `bouwNormUitSjabloon`) en daarna in-place bijgewerkt (`werkNormnotatieBij` migreert de
 * `notationversion`). De `from.content` is de ruwe modeldata van het normmodel; DlModel bewaart die
 * ongemoeid — het zijn de normmatch en `snapNaarNorm` die erin lezen.
 */
export interface NormDocument {
    notationversion: number;
    from: {
        id: string;
        titel: string;
        content: {
            elements: Record<string, Record<string, unknown>>;
            types?: Record<string, unknown>;
        };
    };
    namen: Record<string, number>;
    teller: Record<string, number>;
}

/**
 * De uitkomst van de normtelling: hoeveel elementen het model heeft tegenover wat het normmodel
 * verwacht. Wordt door de `NormService` berekend en hier neergezet; zie de setter van `normtelling`.
 */
export interface NormTelling {
    /**
     * Per elementtype uit `norm.teller`: wat er verwacht wordt, het getelde aantal en het aantal
     * fouten uit de normmatch. `verwacht` gaat sinds golf a5 mee omdat een replaystap tegen de norm
     * van tóen hoort te worden afgebeeld; ontbreekt hij, dan valt de balk terug op `norm.teller`.
     */
    telling: Record<string, { verwacht?: number; aantal: number; fouten: number | null }>;
    /** De namentelling: hoeveel namen het normmodel verwacht en hoeveel er daadwerkelijk kloppen. */
    naamtelling: { norm: number; aantal: number };
    /** Of er een normmatch is; alleen dan zijn de foutaantallen zinvol. */
    heeftMatch: boolean;
    /**
     * Of er over deze stand niets bekend is. Zet een replaystap zonder normblok aan: de cijfers zijn
     * dan niet nul maar onbekend, en de balk dimt in plaats van een foutloos model te suggereren.
     */
    onbekend?: boolean;
}

/**
 * De docentenfeedback-ballon zoals die op een node bewaard wordt. Bewust alleen `tekst` en `auteur`:
 * `modelData()` serialiseert `node.data()` ongefilterd, dus dit is het opslagschema van het
 * model. De editor levert een rijker object aan (het modelcanvas zet ook `auteur_id`, het domein van de
 * gebruiker); dat gaat mee in de logregel en over de lijn, maar wordt niet opgeslagen, precies zoals de
 * ng1-code deed.
 */
export interface Ballon {
    tekst: string;
    auteur: string;
}

/**
 * Eén ballon zoals de ballonlaag van het modelcanvas hem toont: de node, de tekst en de auteur.
 */
export interface BallonWeergave {
    nodeId: string;
    tekst: string;
    auteur: string;
}

@Injectable({providedIn: 'root'})
export class DlModel {

    /**
     * De titel van het model, zoals getoond en als modelnaam in het backend.
     */
    private readonly _titel: WritableSignal<string> = signal('');

    /**
     * Het ingestelde modelleerniveau (0/20/30/40/50); 0 betekent "standaard van het project".
     */
    private readonly _modelleerniveau: WritableSignal<number> = signal(0);

    /**
     * Het berekende modelleerniveau; afgeleid door `OpslagService.bepaalModelleerNiveau()` uit
     * `modelleerniveau` en de projecteigenschappen. Hier alleen opgeslagen, niet berekend.
     */
    private readonly _effectiefModelleerniveau: WritableSignal<number> = signal(0);

    /**
     * De simulatievoorkeur van het model.
     */
    private readonly _simulatiepreferentie: WritableSignal<number> = signal(0);

    /**
     * Of de simulator het snelste-pad-algoritme moet gebruiken; `null` als het project geen
     * overschrijving toestaat.
     */
    private readonly _fastestPath: WritableSignal<boolean | null> = signal(null);

    /**
     * Het normmodel-document (of `false` als er geen norm is). Sinds golf c5 van Stap 4 eigendom van
     * DlModel; `normmatch`/`norminterpretaties`/`telling`/`naamtelling` leven sinds golf 8c in de
     * `NormService`. De getter geeft de LIVE objectreferentie terug (geen kopie), want
     * `werkNormnotatieBij` muteert dat object in-place. Zulke in-place mutaties bumpen het signal niet.
     * Sinds golf e3 is `dl-normbalk` een reactieve consument, maar dat blijft goed: de balk leest
     * `teller` in dezelfde computed als `normtelling`, en die wordt vlak ná `werkNormnotatieBij` gezet
     * (in de laadketen van de `OpslagService`: `werkNormnotatieBij()`, daarna `verwerkNormstand()`).
     */
    private readonly _norm: WritableSignal<NormDocument | false> = signal(false);

    /**
     * De normtelling zoals de normbalk hem toont, of `null` zolang er niets geteld is. Sinds golf 8c
     * gevuld door de `NormService` (die rekent de telling en de fouten uit en zet hier na elke
     * wijziging een vers object neer).
     */
    private readonly _normtelling: WritableSignal<NormTelling | null> = signal(null);

    /*
        De vier toon-vlaggen van de feedbackbalk (golf d2). Documentstate: ze worden als
        `normToon`/`bsToonNoodzakelijk`/`bsToonGewenst`/`simToon` opgeslagen, zitten in de
        undo-snapshots en worden bij het laden teruggezet door `ModelSerialisatie.lees` (sinds golf
        a2 stap 2; daarvoor `_drawGraph` in ng1). De namen volgen die JSON-velden en niet de oude
        ng1-scope-paden: het JSON-veld is het contract.

        De defaults hieronder moeten gelijk blijven aan die van `ModelSerialisatie.lees`, dat op
        dezelfde waarden terugvalt als het veld in het document ontbreekt (oude modellen). Of de
        feedback ook echt getoond wordt hangt daarnaast van sessie-state af (simuleren,
        projectvlaggen); die afweging maakt `DlModelCanvasComponent` (golf f1).
     */

    /** Toont de normfeedback. */
    private readonly _normToon: WritableSignal<boolean> = signal(true);

    /** Toont de noodzakelijke bouwsuggesties. */
    private readonly _bsToonNoodzakelijk: WritableSignal<boolean> = signal(true);

    /** Toont de gewenste bouwsuggesties. */
    private readonly _bsToonGewenst: WritableSignal<boolean> = signal(false);

    /**
     * Toont de simulatiefeedback. De toggle-keten eromheen leeft sinds golf 3c in de
     * `FeedbackbalkService` (uitvoering) en de samenwerk-baas (transport en log).
     */
    private readonly _simToon: WritableSignal<boolean> = signal(false);

    /**
     * Het aantal redenen waarom er in dit model niet bewerkt mag worden; zie `blokkeerBewerken`.
     * Sinds golf a3 woont die stand hier in plaats van als spiegel van een ng1-stack (golf 3c):
     * alle lezers zijn ng21, en de opslagservice leest hem juist — de andere kant op injecteren zou
     * een cyclus geven. Geen documentstate: `reset()` blijft er vanaf, want een blokkade hoort bij
     * de sessie (replay, een kritieke laadfase) en niet bij het document.
     */
    private readonly _bewerkblokkades: WritableSignal<number> = signal(0);

    /**
     * Of er op dit moment een teacher-replay wordt afgespeeld; gezet door de `ReplayService`.
     * Sessiestate, geen documentstate: `reset()` blijft er vanaf.
     */
    private readonly _inReplay: WritableSignal<boolean> = signal(false);

    /**
     * De versie van de ballonnen; zie `ballonversie`.
     */
    private readonly _ballonversie: WritableSignal<number> = signal(0);

    /**
     * Er zijn nieuwe posities gezet; de vlag zegt of het beeld erop bijgesteld moet worden. Zie
     * `bijPositiesGewijzigd`.
     */
    private readonly positiesMelding = new Melding<boolean>();

    /**
     * Luisteraars voor het moment dat de graaf uit een document is opgebouwd; zie `bijGraafGetekend`.
     */
    private readonly graafGetekendLuisteraars: Array<(aanleiding?: Tekenaanleiding) => void | Promise<void>> = [];

    /**
     * Het opslagformaat: schrijven en lezen. Eigen klasse in eigendom van deze (zie daar); de
     * publieke ingang blijft `modelData()`/`tekenGraaf()` hier.
     */
    private readonly serialisatie: ModelSerialisatie;

    /**
     * @param modelcanvas Permanente eigenaar van de model-`cy`; de query-API leest de graaf hieruit.
     * @param translate Voor de vertaalde standaardnaam van elementen zonder eigen naam (bv. een
     *   verse "entity"); DlModel vertaalt zelf, zodat de aanroeper geen vertaalde naam hoeft aan te
     *   leveren.
     * @param samenwerking De samenwerk-sessie-state.
     * @param condtagService Eigenaar van de conditionele tags (golf a2): het opslagformaat en de
     *   conditieset-classes lezen de stand daar.
     */
    constructor(
        private readonly modelcanvas: GlobalModelcanvasService,
        private readonly translate: TranslateService,
        private readonly samenwerking: SamenwerkingService,
        private readonly condtagService: CondtagService
    ) {
        this.serialisatie = new ModelSerialisatie(this, condtagService, modelcanvas);
    }

    /**
     * Zet de document-scalars terug naar hun startwaarde, voor een nieuw/leeg model. Aangeroepen door
     * `OpslagService.reset()`. `titel` doet hier bewust NIET aan mee: die zet de `OpslagService` bij
     * elke reset op een vers vertaalde standaardtitel, en de save vergelijkt de titel daarmee (de
     * "uniqueTitel"-vergelijking). De overige taken van een reset (sessie/samenwerking/undo-buffer,
     * de projectdata-afhankelijke berekening van `effectief_modelleerniveau` via
     * `bepaalModelleerNiveau()`, en `norminterpretaties`/`normmatch` via `NormService.reset()`) doet de
     * `OpslagService.reset()` zelf — zie ook `ontwerp-modelclass.md`. Sinds golf c5 zet `reset()` ook
     * `norm` terug op `false`, en sinds golf e3 de bijbehorende `normtelling`.
     *
     * Sinds golf d2 gaan ook de vier toon-vlaggen van de feedbackbalk terug naar hun default. In de
     * normale gang van zaken zet `tekenGraaf` ze daarna alsnog uit het document; deze reset is het
     * vangnet voor het pad waar het laden op kapotte data afbreekt en `tekenGraaf` dus nooit
     * draait. De view-state van de balk (popup.tekst/hover/overzicht) leeft in de
     * `FeedbackbalkService` en wordt door het modelcanvas teruggezet (`FeedbackbalkService.reset()`,
     * bij elke herlading).
     */
    public reset(): void {
        this.modelleerniveau = 0;
        this.effectief_modelleerniveau = 0;
        this.simulatiepreferentie = 0;
        this.fastest_path = null;
        this.norm = false;
        this.normtelling = null;
        this.normToon = true;
        this.bsToonNoodzakelijk = true;
        this.bsToonGewenst = false;
        this.simToon = false;
    }

    get titel(): string {
        return this._titel();
    }

    set titel(waarde: string) {
        this._titel.set(waarde);
    }

    get modelleerniveau(): number {
        return this._modelleerniveau();
    }

    set modelleerniveau(waarde: number) {
        this._modelleerniveau.set(waarde);
    }

    get effectief_modelleerniveau(): number {
        return this._effectiefModelleerniveau();
    }

    set effectief_modelleerniveau(waarde: number) {
        this._effectiefModelleerniveau.set(waarde);
    }

    get simulatiepreferentie(): number {
        return this._simulatiepreferentie();
    }

    set simulatiepreferentie(waarde: number) {
        this._simulatiepreferentie.set(waarde);
    }

    get fastest_path(): boolean | null {
        return this._fastestPath();
    }

    set fastest_path(waarde: boolean | null) {
        this._fastestPath.set(waarde);
    }

    get norm(): NormDocument | false {
        return this._norm();
    }

    set norm(waarde: NormDocument | false) {
        this._norm.set(waarde);
    }

    get normtelling(): NormTelling | null {
        return this._normtelling();
    }

    /**
     * De telling is afgeleid van het model, geen documentstate.
     */
    set normtelling(waarde: NormTelling | null) {
        this._normtelling.set(waarde);
    }

    get normToon(): boolean {
        return this._normToon();
    }

    set normToon(waarde: boolean) {
        this._normToon.set(waarde);
    }

    get bsToonNoodzakelijk(): boolean {
        return this._bsToonNoodzakelijk();
    }

    set bsToonNoodzakelijk(waarde: boolean) {
        this._bsToonNoodzakelijk.set(waarde);
    }

    get bsToonGewenst(): boolean {
        return this._bsToonGewenst();
    }

    set bsToonGewenst(waarde: boolean) {
        this._bsToonGewenst.set(waarde);
    }

    get simToon(): boolean {
        return this._simToon();
    }

    set simToon(waarde: boolean) {
        this._simToon.set(waarde);
    }

    /**
     * Of het model volledig readonly is: er staat een bewerkblokkade óf er is geen verbinding met het
     * backend. Dezelfde samenstelling als ng1's `model.fullReadonly`-getter altijd had. Guard voor de
     * balk-toggles, de simfeedback-klik, de chat en de hele opslagketen.
     *
     * De enige die een blokkade zet is de `ReplayService`, dus in de praktijk: tijdens het bladeren
     * door een replay, en zolang de verbinding weg is. **Niet** bij meekijken — dat stond hier tot
     * 23-09-2026 wel, en op die aanname is een testbeschrijving stukgelopen.
     */
    get fullReadonly(): boolean {
        return this._bewerkblokkades() > 0 || !this.samenwerking.verbonden();
    }

    /**
     * Houdt het bewerken tegen: er is een reden bij gekomen (replay, een kritieke laad- of
     * savefase). Een teller en geen boolean, want er kunnen er meer tegelijk zijn — zet er net zo
     * vaak `laatBewerkenLos()` tegenover.
     */
    public blokkeerBewerken(): void {
        this._bewerkblokkades.update(aantal => aantal + 1);
    }

    /**
     * Haalt één reden weg. Klemt op nul: `OpslagService.reset()` laat onvoorwaardelijk los, ook op een
     * lege stapel, en dat hoort dan een no-op te zijn.
     */
    public laatBewerkenLos(): void {
        this._bewerkblokkades.update(aantal => Math.max(0, aantal - 1));
    }

    /**
     * Of er een teacher-replay wordt afgespeeld. De samenwerk-baas negeert binnenkomende berichten
     * zolang dit aanstaat (§ 5 van `ontwerp-apiservice.md`), de registratie stuurt de vlag mee zodat
     * de verdeler een replayer geen leider maakt, en de bouwsuggestie-engine draait niet.
     */
    public readonly inReplay: Signal<boolean> = this._inReplay.asReadonly();

    /**
     * Zet de replaystand; aangeroepen door de `ReplayService` (tot golf a5 deed ng1 dat via
     * `model.inReplay`).
     */
    public zetInReplay(inReplay: boolean): void {
        this._inReplay.set(!!inReplay);
    }

    /* ***************** READONLY QUERY-API (golf b) ***************** */
    // Pure leesfuncties over de graaf, 1-op-1 geport uit `cytocanvas.modelview.controller.js`. De
    // `cy` komt telkens uit de permanente GlobalModelcanvasService, zodat de API na een model-wissel
    // meteen tegen het actieve canvas werkt.

    /** Selector voor alle quantity-inequalities. */
    private readonly qIneqfilter = '[type="q_ineq_lt"],[type="q_ineq_lte"],[type="q_ineq_eq"],[type="q_ineq_gte"],[type="q_ineq_gt"]';

    /** Selector voor alle derivative-inequalities. */
    private readonly dIneqfilter = '[type="d_ineq_lt"],[type="d_ineq_lte"],[type="d_ineq_eq"],[type="d_ineq_gte"],[type="d_ineq_gt"]';

    /** Selector voor alle inequalities (quantity + derivative). */
    private readonly ineqfilter = this.qIneqfilter + ',' + this.dIneqfilter;

    /**
     * Geeft een cy-element uit het modelcanvas terug.
     */
    public getElement(id: string): CollectionReturnValue {
        return this.cy.getElementById(id);
    }

    /**
     * Geef een calc terug van een specifiek type, tussen de opgegeven elementen (ids).
     */
    public getCalcFromTo(calctype: string, from: string, to: string): NodeCollection {
        return this.cy.nodes(`[type="${calctype}"][from="${from}"][to ="${to}"]`);
    }

    /**
     * Geef de quantitynode die bij het argument (subelement of quantity) hoort, of `false`.
     */
    public getQuantity(subElement: NodeSingular | string): NodeSingular | false {
        return this.getNodeUp(subElement, ['quantity']);
    }

    /**
     * Geeft de quantityspace-node voor een qnode, of `null` als die er niet is.
     */
    public getQSNode(qNode: NodeSingular | string): NodeCollection | null {
        const cy = this.cy;
        const node = typeof qNode !== 'object' ? cy.getElementById(qNode) as unknown as NodeSingular : qNode;
        if (!node) {
            return null;
        }
        const qs = this.outElements(node, '[type = "quantity_space"]');
        return qs.empty() ? null : qs;
    }

    /**
     * Zoekt naar beneden tot de hele derivative-qs er is; geeft een object met per type de node.
     */
    public derivativeQs(qNode: NodeSingular | string): Record<string, NodeCollection> {
        const cy = this.cy;
        const node = typeof qNode !== 'object' ? cy.getElementById(qNode) as unknown as NodeSingular : qNode;
        let item: NodeSingular | NodeCollection = this.outElements(node, '[type = "derivative"]');
        const res: Record<string, NodeCollection> = {};
        let next: NodeCollection;
        while ((next = this.outElements(item, '[type = "derivative_plus"],[type = "derivative_zero"],[type = "derivative_min"]')).length) {
            item = next.first();
            res[next.data('type') as string] = next;
        }
        return res;
    }

    /**
     * Geeft het volgende quantityspace-element onder een qsnode of qs-element, of `null`.
     */
    public nextQsItem(node: NodeSingular): NodeCollection | null {
        const next = this.outElements(node, '[type = "quantity_space_interval"],[type = "quantity_space_point"]');
        return next.length ? next.first() : null;
    }

    /**
     * Geeft het vorige quantityspace-element (nooit de qsnode zelf), of `null`.
     */
    public prevQsItem(node: NodeSingular): NodeCollection | null {
        const prev = this.inElements(node, '[type = "quantity_space_interval"],[type = "quantity_space_point"]');
        return prev.length ? prev.first() : null;
    }

    /**
     * Levert de zero-node in de quantityspace van de quantity op, of `null`.
     */
    public getZeroValueNode(qNode: NodeSingular | string): NodeCollection | null {
        const zero = this.getFullQs(qNode).filter('[?isZero]');
        return zero.empty() ? null : zero;
    }

    /**
     * Vindt een zeronode in de buurt: liefst bij de quantity zelf, anders via een ineq naar een
     * andere QS, en als laatste redmiddel een willekeurige zero in het model. `false` als er geen is.
     */
    public relatedZeroNode(qNode: NodeSingular): NodeSingular | NodeCollection | false {
        const cy = this.cy;
        let zero: NodeCollection | null = this.getZeroValueNode(qNode); // gewoon bij zichzelf
        if (zero) {
            return zero;
        }
        // alle quantities waarmee de Q of een q-value verbonden is middels een ineq
        const qMetVals = (qNode as unknown as {add: (c: NodeCollection) => NodeCollection}).add(this.getFullQs(qNode));
        const ineqs = this.outElements(qMetVals, this.qIneqfilter);
        zero = this.outElements(ineqs, ['?isZero']); // directe zero
        if (zero && zero.nonempty()) {
            return zero.first();
        }
        // q met zero of sub van q met zero
        this.outElements(ineqs).forEach((n) => {
            const q = this.getQuantity(n as unknown as NodeSingular);
            if (q && (zero = this.getZeroValueNode(q))) {
                return false; // break
            }
            return undefined;
        });
        if (zero && zero.nonempty()) {
            return zero.first();
        }
        // laatste: een willekeurig element dat zero is
        zero = cy.nodes('[type="quantity_space_point"][?isZero]');
        return zero && zero.nonempty() ? zero.first() : false;
    }

    /**
     * Geeft een inequality tussen twee elementen, of `false` als die er niet is.
     * @param node1
     * @param node2
     * @param ookconditiesets Als `true`, dan ook een ineq uit een conditieset.
     */
    public getIneqBetween(node1: NodeSingular, node2: NodeSingular, ookconditiesets?: boolean): NodeSingular | false {
        const n1 = node1.id(), n2 = node2.id();
        let res = this.cy.nodes(this.ineqfilter)
            .filter('[from="' + n1 + '"][to="' + n2 + '"],[from="' + n2 + '"][to="' + n1 + '"]')
            .not('.simulation');
        if (!ookconditiesets) {
            res = res.filter('[!condtag]');
        }
        return res.empty() ? false : res.first() as unknown as NodeSingular;
    }

    /**
     * Alle quantitynodes in het model.
     */
    public quantities(): CollectionReturnValue {
        return this.cy.$('[type = "quantity"]');
    }

    /**
     * Alle ineqs in het model (exclusief simulatie-ineqs).
     */
    public modelIneqs(): CollectionReturnValue {
        return this.cy.$(this.ineqfilter).not('.simulation');
    }

    /**
     * Alle (of gefilterde) simulatie-content op het modelcanvas.
     */
    public simulationContent(filter?: string): CollectionReturnValue {
        const alles = this.cy.$('.simulation');
        return filter ? alles.filter(filter) : alles;
    }

    /**
     * Controleert of een node op de gewenste positie zou overlappen met een bestaande, zichtbare node.
     */
    public overlap(pos: Position, marge: number): boolean {
        let overlap = false;
        this.cy.nodes(':visible').forEach((node) => {
            const npos = node.position();
            if ((pos.x - marge) < npos.x && (pos.x + marge) > npos.x && (pos.y - marge) < npos.y && (pos.y + marge) > npos.y) {
                overlap = true;
                return false; // break
            }
            return undefined;
        });
        return overlap;
    }

    /* ***************** SCHRIJF-API (golf c1) ***************** */
    // Basisprimitieven voor het opbouwen van de graaf (node-/edge-classes, edges, edgehandles),
    // 1-op-1 geport uit `cytocanvas.modelview.controller.js`. Puur graafmutatie: de staart
    // (undo/save/samenwerking/logging) doet de actietabel van de samenwerk-baas. De condtag-class-
    // afleiding (`updateConditieclasses`) is in golf d1 naar deze class verhuisd; de condtag-stand zelf
    // is van de `CondtagService`, en `condtag`/`condstate` geeft de aanroeper als parameter mee.

    /**
     * Geef gegeven een nodeType de juiste styleclasses (cytostyle) terug, zo maken we de classes
     * onafhankelijk van lay-outnamen en kunnen we bij inlezen ook de classes opnieuw zetten gegeven
     * de nieuwe inzichten. Classes zijn altijd: nodetype, en voor elke parent in de hierarchie isa_...
     */
    public nodeClasses(nodeType: string): string {
        const classes = [nodeType, 'model'];
        for (const parent of elements.alleUps(nodeType)) {
            classes.push('isa_' + parent);
        }
        if (elements.getDefinition(nodeType).hideInSimulate) {
            classes.push('hide_in_simulate');
        }
        return classes.join(' ');
    }

    /**
     * Verwijdert de edge tussen twee elementen (gezocht op `orig_source`/`orig_target`, zodat het
     * ook goed gaat als er edgehandles gebruikt zijn).
     */
    public deleteEdge(sourceNode: NodeSingular, targetNode: NodeSingular): void {
        this.cy.remove(this.cy.$(`[orig_source='${sourceNode.id()}'][orig_target='${targetNode.id()}']`));
    }

    /**
     * Legt een edge aan tussen twee elementen (via eventuele edgehandles) en herberekent meteen de
     * edgehandle-posities. Ook gebruikt door de simulatiecode.
     */
    public addEdgeFromTo(from: NodeSingular, to: NodeSingular, reltype: string, stateclass: string): CollectionReturnValue {
        const tohandle = this.getEdgeHandle(to, 'to_' + reltype);
        const fromhandle = this.getEdgeHandle(from, 'from_' + reltype);
        const condstate = from.data('condstate') || to.data('condstate'); // 1 van 2en bepaalt het
        const cyedge = this.cy.add({
            group: 'edges',
            data: {
                source: fromhandle.id(),
                target: tohandle.id(),
                // orig source en target ook, zie deleteEdge
                orig_source: from.id(),
                orig_target: to.id(),
                reltype,
                id: this.genereerId('e'),
                ...(condstate ? {condstate} : {})
            },
            classes: this.edgeClasses(from.data('type'), to.data('type'), reltype, stateclass)
        });
        this.repositionEdgeHandles();
        return cyedge;
    }

    /**
     * Voegt een collectie simulatienodes toe aan het canvas en markeert ze als simulatie.
     * De aanroeper (de tekenaar van het simcanvas, `SimResultaatTekenaar`) verzorgt zelf eventuele
     * delete-UI en scratch-boekhouding. Geeft de aangemaakte collectie terug zodat de aanroeper kan
     * doorketenen.
     */
    public addSimnodes(nodes: ElementDefinition | ElementDefinition[]): CollectionReturnValue {
        const created = this.cy.add(nodes);
        created.addClass('simulation');
        return created;
    }

    /* ***************** SCHRIJF-API (golf 5a) ***************** */
    // Afronding van de simnode-schrijfkant (naast `addSimnodes`/`simulationContent`): het opruimen
    // van simulatiecontent en het aan/uit zetten van de `simulating`-weergaveclass. Puur
    // `cy`-mutatie, simulatie-transiënt.

    /**
     * Verwijdert alle of gefilterde simulatiecontent van het modelcanvas.
     * @param filter extra selectorfilter op de content
     */
    public removeSimulation(filter?: string): void {
        this.cy.remove(this.simulationContent(filter));
    }

    /**
     * Zet of verwijdert de `simulating`-class op alle nu beschikbare elementen, om aan te geven dat
     * er een simulatie-state weergegeven wordt.
     */
    public setInSimulate(inSimulate: boolean): void {
        if (inSimulate) {
            this.cy.elements().addClass('simulating');
        } else {
            this.cy.elements().removeClass('simulating');
        }
    }

    /**
     * Bij het verwijderen van een edge: kijk of er edgehandles in het spel waren en plan hun
     * verwijdering (na de huidige cy-operatie, om recursie te voorkomen).
     */
    public checkEdgeHandleOnDeleteEdge(edge: EdgeSingular): void {
        edge.connectedNodes('[type="edgeHandle"]').forEach((edgeHandle) => {
            setTimeout(() => this.cy.remove(edgeHandle), 0);
        });
    }

    /**
     * Loop over alle edgehandles en bepaal opnieuw hun optimale offset (als ze er meerdere hebben).
     * Aangeroepen na automove en bij nieuwe edgehandles.
     */
    public repositionEdgeHandles(): void {
        this.cy.nodes('.edgeHandle').forEach((edgeNode) => {
            const parentNode = this.cy.getElementById(edgeNode.data('handleParent') as string);
            let offsets = edgeNode.scratch('offsets') as Array<{x: number; y: number}> | undefined;
            if (!offsets) {
                const handledef = elements.getDefinition(parentNode.data('type') as string).edgeHandles;
                if (!(handledef && handledef[edgeNode.data('handleType') as string])) {
                    return;
                }
                offsets = handledef[edgeNode.data('handleType') as string];
                edgeNode.scratch('offsets', offsets);
            }
            if (!offsets || offsets.length < 2) {
                return;
            }
            const linked = edgeNode.neighborhood('node');
            if (linked.empty()) {
                return;
            }
            const lpos = linked.position();
            const ppos = parentNode.position();
            //de index hoort erbij: `zetPosities` zet een handle via zijn huidige offset mee. Tot golf 4d
            //ontbrak hij, en omdat cytoscape `scratch(naam, undefined)` als lezen opvat, bleef
            //`currentOffset` op zijn beginwaarde staan
            let best: {hpos: Position; lsquare: number; index: number} | null = null;
            for (const [index, o] of offsets.entries()) {
                const hpos = {x: ppos.x + o.x, y: ppos.y + o.y};
                const lsquare = Math.pow(lpos.x - hpos.x, 2) + Math.pow(lpos.y - hpos.y, 2); // pythagoras, sortering telt
                if (!best || lsquare < best.lsquare) {
                    best = {hpos, lsquare, index};
                }
            }
            if (best) {
                edgeNode.position(best.hpos);
                edgeNode.scratch('currentOffset', best.index);
            }
        });
    }

    /* ***************** SCHRIJF-API (golf c2) ***************** */
    // Node-/relatie-CRUD, 1-op-1 geport uit `cytocanvas.modelview.controller.js`. De staart
    // (undo/save/samenwerking/logging) doet de samenwerk-baas; `condtag`/`condstate` bepaalt de
    // aanroeper, en die zet daarna ook de conditieset-classes (`updateConditieclasses`). Het label
    // zet het modelcanvas bij elke `add` op de graaf (`zetNodelabel`).

    /**
     * Voegt een nieuwe node toe aan de graaf: vult de basisvelden aan (classes, definitie, effectief
     * modelleerniveau, parent-/child-registratie, standaardnaam, gegenereerd id), voegt hem toe aan
     * `cy` en trekt zo nodig een lijn naar de parent. Selecteert de node (via een deferred `select()`
     * om een stille deselect elders in de flow te vermijden — zie de select-race-bugfix) als
     * `select` gegeven is.
     */
    public addCyNode(nodeData: NieuweNodeData, parentNode: NodeSingular | null, select?: boolean): NodeSingular {
        nodeData.group = 'nodes';
        if (!nodeData.scratch) {
            nodeData.scratch = {};
        }
        const def = elements.getDefinition(nodeData.data.type);
        nodeData.scratch['definition'] = def;
        nodeData.classes = this.nodeClasses(nodeData.data.type);
        nodeData.data['mln'] = this.effectief_modelleerniveau;
        nodeData.data['childIds'] = [];
        nodeData.data['parentId'] = parentNode ? parentNode.id() : false;
        if (!nodeData.data.nodename) {
            // def.nodename is de rauwe (onvertaalde) sleutel uit de kale elements-lib, of `false` als
            // dit elementtype geen naam heeft — alleen in het eerste geval vertalen.
            nodeData.data.nodename = def.nodename ? this.translate.instant(def.nodename) : def.nodename;
        }
        if (!nodeData.data.id) {
            nodeData.data.id = this.genereerId('n');
        }
        // De select-parameter is leidend: een aangeleverde json (bv. `newnode.json()` van de leider
        // in het create-samenwerkbericht) kan zelf al selected: true bevatten, en dat zou bij een
        // volger de node selecteren en zo het naam-inputveld openen.
        nodeData.selected = !!select;
        if (select) {
            nodeData.scratch[NET_GEPLAATST] = true; //wie plaatst, krijgt meteen het naamveld
        }
        const node = this.cy.add(nodeData as unknown as ElementDefinition) as unknown as NodeSingular;
        if (parentNode) {
            (parentNode.data('childIds') as string[]).push(node.id());
        }
        if (parentNode && (def as {connecttoparent?: boolean}).connecttoparent) {
            this.addEdgeFromTo(parentNode, node, 'parentchild', 'model');
        }
        if (select) {
            setTimeout(() => node.select(), 0);
        }
        return node;
    }

    /**
     * Voegt een relatie toe tussen twee elementen: berekent de tussenpositie, maakt de relatienode
     * (via `addCyNode`, incl. diens vertaalde standaardnaam voor relatietypen die er een hebben, bv.
     * "configuration") en trekt de twee lijnen naar `van`/`naar`. `condtag`/`condstate` moeten door de
     * caller al bepaald zijn. Speciaal geval (afgesproken met Bert Bredeweg d.d. 7-2-2016): als een
     * van de argumenten een calc is, blijft het geheel zichtbaar in simulate.
     */
    public voegRelatieToe(
        van: NodeSingular, naar: NodeSingular, type: string, nieuweId: string | undefined, selecteer: boolean | undefined,
        condtag: string | null, condstate: string | null
    ): NodeSingular {
        const startNodepos = van.position();
        const endNodepos = naar.position();
        const dy = (elements.isSubtypeOf(type, 'causal') || type === 'configuration') ? -100 : 100;
        const x = startNodepos.x + (endNodepos.x - startNodepos.x) / 2;
        const y = startNodepos.y + (endNodepos.y - startNodepos.y) / 2 + dy;
        const relNode = this.addCyNode({
            data: {
                type,
                from: van.id(),
                to: naar.id(),
                id: nieuweId || undefined,
                condtag,
                condstate
            },
            position: {x, y}
        }, null, selecteer);

        const starttype = van.data('type') as string;
        const endttype = naar.data('type') as string;
        if (elements.isSubtypeOf(starttype, 'q_calc') || elements.isSubtypeOf(starttype, 'd_calc') ||
            elements.isSubtypeOf(endttype, 'q_calc') || elements.isSubtypeOf(endttype, 'd_calc')) {
            relNode.removeClass('hide_in_simulate');
        }

        this.addEdgeFromTo(van, relNode, 'source', 'model');
        this.addEdgeFromTo(naar, relNode, 'target', 'model');
        return relNode;
    }

    /**
     * Draait de argumenten (`from`/`to`) van een relatienode om, incl. het herwiren van de edges.
     */
    public wisselArgumenten(node: NodeSingular): void {
        const oudefrom = this.getElement(node.data('from') as string) as unknown as NodeSingular;
        const oudeto = this.getElement(node.data('to') as string) as unknown as NodeSingular;
        this.deleteEdge(oudefrom, node);
        this.deleteEdge(oudeto, node);
        node.data('from', oudeto.id());
        this.addEdgeFromTo(oudeto, node, 'source', 'model');
        node.data('to', oudefrom.id());
        this.addEdgeFromTo(oudefrom, node, 'target', 'model');
    }

    /**
     * Wijzigt het type van een node: haalt eerst de oude edges weg (als het een relatie was), zet het
     * nieuwe type en de bijbehorende classes, en trekt de edges opnieuw (als het nog steeds, of nu
     * pas, een relatie is).
     */
    public wijzigNodeType(node: NodeSingular, type: string): void {
        let from: NodeSingular | undefined;
        let to: NodeSingular | undefined;
        if (elements.isRelation(node.data('type'))) {
            from = this.getElement(node.data('from') as string) as unknown as NodeSingular;
            to = this.getElement(node.data('to') as string) as unknown as NodeSingular;
            this.deleteEdge(from, node);
            this.deleteEdge(to, node);
        }
        node.data('type', type);
        node.classes(this.nodeClasses(type));
        if (elements.isRelation(type) && from && to) {
            this.addEdgeFromTo(from, node, 'source', 'model');
            this.addEdgeFromTo(to, node, 'target', 'model');
        }
    }

    /**
     * Verwijdert een node en (tenzij `skipRecursive`) al zijn `outElements` (subelementen en
     * relatienodes die deze node als argument hebben) recursief. Werkt de `childIds`-boekhouding van
     * de parent bij en ruimt eventuele edgehandles op. Roept, indien gegeven, `onNodeVerwijderd` aan
     * vlak na het verwijderen van elke node — bedoeld voor een aanroeper die per verwijderde node iets
     * moet doen dat verder niets met de graafmutatie te maken heeft (vroeger de
     * bouwsuggestie-notificatie van ng1; de huidige aanroepers geven hem niet mee).
     */
    public deleteNode(node: NodeSingular, skipRecursive?: boolean, onNodeVerwijderd?: (node: NodeSingular) => void): void {
        const nodeId = node.id();
        const targets = this.outElements(node, 'node');
        node.unselect();
        const parentId = node.data('parentId') as string | undefined;
        if (parentId) {
            const parentNode = this.cy.nodes('#' + parentId);
            if (parentNode.nonempty()) {
                const childIds = parentNode.data('childIds') as string[];
                childIds.splice(childIds.indexOf(node.id()), 1);
            }
        }
        this.cy.remove(node);
        onNodeVerwijderd?.(node);
        this.cy.remove(this.cy.nodes(`.edgeHandle[handleParent="${nodeId}"]`));
        if (!skipRecursive) {
            targets.forEach((subnode) => {
                this.deleteNode(subnode, false, onNodeVerwijderd);
            });
        }
    }

    /* ***************** SCHRIJF-API (golf c3) ***************** */
    // Quantity-/quantity-space-CRUD, 1-op-1 geport uit `cytocanvas.modelview.controller.js`. De staart
    // (undo/save/samenwerking/logging) doet de samenwerk-baas. `condtag`/`condstate` moeten door de
    // caller al bepaald zijn (net als bij `voegRelatieToe`, golf c2) — quantity/quantity-space/derivative-
    // elementen zijn geen `neverConditional`-types, dus dit moet wél worden doorgegeven. De
    // conditieset-classes van elke nieuwe node zet de aanroeper op de hier teruggegeven node(s)
    // (`updateConditieclasses`, in de modelacties); het label zet het modelcanvas bij de `add`.

    /**
     * Voegt een quantity toe aan `entity`, inclusief zijn afgeleide (`derivative` + de drie
     * waarde-elementen).
     */
    public addQuantity(
        entity: NodeSingular, pos: Position, selecteer: boolean, condtag: string | null, condstate: string | null
    ): {quantity: NodeSingular; subnodes: NodeSingular[]} {
        const quantity = this.addCyNode({
            data: {type: 'quantity', condtag, condstate},
            position: pos
        }, entity, selecteer);
        const subnodes = this.addDerivative(quantity, {x: pos.x - 45, y: pos.y + 45}, condtag, condstate);
        return {quantity, subnodes};
    }

    /**
     * Voegt een quantity-space toe aan `qNode` met het eerste element (point of interval,
     * `firstElementType`). Geeft `null` als er al een quantity-space is.
     */
    public addQuantitySpace(
        qNode: NodeSingular, position: Position, firstElementType: string | undefined, selecteer: boolean,
        condtag: string | null, condstate: string | null
    ): {qsNode: NodeSingular; qse: NodeSingular} | null {
        if (this.outElements(qNode, '[type = "quantity_space"]').nonempty()) {
            return null;
        }
        const qsNode = this.addCyNode({
            data: {type: 'quantity_space', automoveorder: 999, condtag, condstate},
            position
        }, qNode, false);
        qsNode.data('automovegroup', 'qsgroup-' + qsNode.id());
        const qse = this.addQuantitySpaceElement(qsNode, 'top', selecteer, firstElementType, undefined, condtag, condstate);
        return {qsNode, qse};
    }

    /**
     * Voegt een point of interval toe aan een (bestaande) quantity-space, tussen de juiste buren
     * (`topOrBottom`): elk volgend element wordt een kind van het vorige. Puur intern: geen
     * undo/save, de aanroeper bewaakt dat.
     */
    public addQuantitySpaceElement(
        qsNode: NodeSingular, topOrBottom: string, selectItem: boolean, elementType: string | undefined,
        id: string | undefined, condtag: string | null, condstate: string | null
    ): NodeSingular {
        let parentNode: NodeSingular = qsNode;
        let firstChild: NodeSingular | null = null;
        if (topOrBottom === 'top') {
            firstChild = this.nextQsItem(qsNode) as unknown as NodeSingular | null;
        } else {
            let sub: NodeSingular | null;
            do {
                sub = this.nextQsItem(parentNode) as unknown as NodeSingular | null;
                if (sub) {
                    parentNode = sub;
                }
            } while (sub);
        }

        let newpos: Position;
        if (firstChild) {
            elementType = firstChild.data('type') === 'quantity_space_interval' ? 'quantity_space_point' : 'quantity_space_interval';
            newpos = {...firstChild.position()};
        } else if (parentNode !== qsNode) {
            elementType = parentNode.data('type') === 'quantity_space_interval' ? 'quantity_space_point' : 'quantity_space_interval';
            newpos = {x: parentNode.position().x, y: parentNode.position().y + 30};
        } else {
            elementType = elementType || 'quantity_space_point';
            newpos = {x: parentNode.position().x, y: parentNode.position().y + 60};
        }

        const qsNodeId = qsNode.id();
        const elementNode = this.addCyNode({
            data: {
                type: elementType,
                qspace: qsNodeId,
                childIds: [],
                id,
                parentId: false,
                automovegroup: qsNode.data('automovegroup'),
                condtag,
                condstate
            },
            position: newpos
        }, null, selectItem);

        if (firstChild) {
            this.deleteEdge(parentNode, firstChild);
            const childIds = parentNode.data('childIds') as string[];
            childIds.splice(childIds.indexOf(firstChild.id()), 1);
        }
        this.addEdgeFromTo(parentNode, elementNode, 'normal', 'model');
        (parentNode.data('childIds') as string[]).push(elementNode.id());
        elementNode.data('parentId', parentNode.id());
        if (firstChild) {
            this.addEdgeFromTo(elementNode, firstChild, 'normal', 'model');
            (elementNode.data('childIds') as string[]).push(firstChild.id());
            firstChild.data('parentId', elementNode.id());
        }

        this.reorderQs(qsNode);
        return elementNode;
    }

    /**
     * Verwijdert het bovenste of onderste element van een quantity-space (het enige toegestane
     * geval, zie `magQsElementWeg`) en koppelt zo nodig het
     * onderliggende element rechtstreeks aan de quantity-space, en herpositioneert de rest.
     */
    public deleteQuantitySpaceElement(node: NodeSingular): void {
        const qsNode = this.getNodeUp(node, ['quantity_space']) as NodeSingular;
        const parent = this.prevQsItem(node) as unknown as NodeSingular | null;
        const child = this.nextQsItem(node) as unknown as NodeSingular | null;

        if (parent && !child) {
            this.deleteNode(node);
        } else if (child && !parent) {
            this.deleteEdge(qsNode, node);
            this.deleteEdge(node, child);
            child.position({...node.position()});
            this.addEdgeFromTo(qsNode, child, 'normal', 'model');
            (qsNode.data('childIds') as string[]).push(child.id());
            child.data('parentId', qsNode.id());
            this.deleteNode(node);
        }
        this.reorderQs(qsNode);
    }

    /**
     * Zorgt dat de elementen van een quantity-space na toevoegen of verwijderen weer netjes onder
     * elkaar staan (ook gebruikt door `snapNaarNorm`).
     */
    public reorderQs(qsNode: NodeSingular): void {
        let qsEl = this.nextQsItem(qsNode) as unknown as NodeSingular;
        const x = qsEl.position().x;
        let y = qsEl.position().y;
        (this.outElements(qsEl, '[type = "quantity_value"]') as unknown as NodeSingular).position('y', y);
        while ((qsEl = this.nextQsItem(qsEl) as unknown as NodeSingular)) {
            y += 30;
            qsEl.position({x, y});
            (this.outElements(qsEl, '[type = "quantity_value"]') as unknown as NodeSingular).position('y', y);
        }
    }

    /**
     * Zet of ontzet de `isZero`-vlag op een quantity-space-punt, incl. de bijbehorende
     * `skipName`-vlag (blokkeert naamwijziging zolang het punt zero is).
     */
    public zetIsZero(node: NodeSingular, waarde: boolean): void {
        node.data('isZero', waarde);
        node.data('skipName', waarde);
        this.zetNodelabel(node); //de naam wordt hiermee getoond of juist onderdrukt
    }

    /**
     * Zet het canvaslabel van een node: de nodenaam als het elementtype er een heeft en de naam niet
     * onderdrukt is (`skipName`, de zero van een quantity space), anders leeg. Speciale namen
     * (`___…`) worden vertaald.
     *
     * Staat hier en niet bij het canvas omdat elke plek die `nodename` of `skipName` verandert hem
     * meteen moet bijwerken; in ng1 stond daarvoor na elke mutatie een losse `updateNodelabel`, en
     * die reeks is met golf a4 vervallen. Het canvas roept hem nog aan voor nodes die van buiten
     * binnenkomen (het tekenen van een document, een `add` op de graaf).
     */
    public zetNodelabel(node: NodeSingular): void {
        const def = elements.getDefinition(node.data('type') as string);
        const naam = def.hasname && !node.data('skipName') ? (node.data('nodename') as string) : '';
        node.data('label', specialenaam(naam, this.translate));
    }

    /* ***************** SCHRIJF-API (golf c4) ***************** */
    // Naam-wijziging en posities, 1-op-1 geport uit `cytocanvas.modelview.controller.js`. De staart
    // (undo/save/samenwerking/logging) doet de samenwerk-baas; het label zet `wijzigNaam` zelf.

    /**
     * Wijzigt de naam van een node: normaliseert de invoer (trimmen en dubbele spaties samenvouwen) en
     * schrijft die naar `nodename`. Geeft de genormaliseerde naam terug, zodat de aanroeper (loggen,
     * samenwerkbericht) dezelfde waarde gebruikt als wat daadwerkelijk in de graaf staat.
     */
    public wijzigNaam(node: NodeSingular, naam: string): string {
        const genormaliseerd = naam.trim().replace(/\s{2,}/g, ' ');
        node.data('nodename', genormaliseerd);
        this.zetNodelabel(node);
        return genormaliseerd;
    }

    /**
     * Zet de posities van de opgegeven nodes (`{id, pos}`) en verplaatst hun eventuele edgehandles mee
     * (via de opgeslagen offset), gevolgd door een volledige herberekening met `repositionEdgeHandles`.
     * Ontbrekende nodes worden overgeslagen. Geeft terug of er minstens één niet-simulatie-node bewoog,
     * zodat de aanroeper weet of het model moet worden opgeslagen (een pure simulatie-move niet).
     * @param posities de nodes die verplaatst worden
     * @param [fit] het model is in zijn geheel verschoven (snapNorm), dus het beeld moet mee
     */
    public zetPosities(posities: Array<{id: string; pos: Position}>, fit = false): boolean {
        let nietsimulatiemove = false;
        for (const nodepos of posities) {
            // een ontbrekende node geeft een lege collectie terug; die slaan we over
            const node = this.getElement(nodepos.id) as unknown as NodeSingular;
            if (node.empty()) {
                console.warn('zetPosities: ontbrekende node geskipt', nodepos);
                continue;
            }
            if (!node.hasClass('simulation')) {
                nietsimulatiemove = true;
            }
            node.position(nodepos.pos);
            // edgehandles van deze node meeverplaatsen op basis van hun huidige offset
            this.cy.nodes('.edgeHandle[handleParent="' + node.id() + '"]').forEach((eh) => {
                const offset = (eh.scratch('offsets') as Array<{x: number; y: number}>)[eh.scratch('currentOffset') as number];
                eh.position({
                    x: nodepos.pos.x + offset.x,
                    y: nodepos.pos.y + offset.y
                });
            });
        }
        this.repositionEdgeHandles(); // en nu nog een keer goed uitrekenen
        this.positiesMelding.meld(fit);
        return nietsimulatiemove;
    }

    /**
     * Abonneert op nieuwe posities (`zetPosities`: een sleep, snapNorm, of posities van de leider). De
     * tekstbeschrijving sorteert zijn regels op de plek van de nodes en luistert hierop; het canvas
     * brengt op de `fit`-vlag zijn beeld op maat. Geeft de afmeldfunctie terug.
     */
    public bijPositiesGewijzigd(luisteraar: (fit: boolean) => void): () => void {
        return this.positiesMelding.abonneer(luisteraar);
    }

    /**
     * Schuift het model naar de vorm van het normmodel: elk element dat de normmatch op een normelement
     * gemapt heeft, komt op de plek van dat element te staan. Een element zonder eigen mapping (een
     * attribuut, een assumptie, …) schuift mee met zijn parent; de waarden van een quantity space en een
     * afgeleide komen op hun vaste plek onder hun ouder, en exogenen en de allewaarden-markering naast hun
     * grootheid. Daarna gaan de posities langs `zetPosities`, dat de edgehandles meeneemt.
     *
     * Port van `$scope.snapNorm` uit de AngularJS-modelview (golf 4d); het undo-punt, het rondsturen en
     * het opslaan doet de samenwerk-baas eromheen.
     * @param mappingsOpWerk de mapping per werkelement uit de huidige normmatch
     * @returns de posities die gezet zijn, in de volgorde waarin ze bepaald werden
     */
    public snapNaarNorm(mappingsOpWerk: Record<string, NormmatchMapping>): Array<{id: string; pos: Position}> {
        const norm = this.norm;
        if (!norm) {
            return [];
        }
        const cy = this.cy;
        //deze types hebben een eigen mapping; de rest wordt ten opzichte van zijn parent geplaatst
        const mapbaar = ['topelement', 'quantity', 'quantity_space', 'derivative', 'relation'];
        //en deze krijgen een vaste plek bij hun ouder, of zijn hulpelementen (edgehandles)
        const eigenPlek = ['derivative_element', 'quantity_space_element', 'q_exo', 'quantity_allvalues', 'noElement'];
        const verplaatst: string[] = [];
        const noteer = (node: NodeSingular | NodeCollection): void => {
            for (const element of node.toArray()) {
                if (!verplaatst.includes(element.id())) {
                    verplaatst.push(element.id());
                }
            }
        };
        //de plek van elke node vóór het schuiven: een kind houdt zijn afstand tot zijn parent
        const vooraf: Record<string, Position> = {};
        cy.nodes().forEach(node => {
            vooraf[node.id()] = {...node.position()};
        });

        const naplaatsen: NodeSingular[] = [];
        cy.nodes().forEach(node => {
            const type = node.data('type') as string;
            if (!mapbaar.some(t => elements.isSubtypeOf(type, t))) {
                if (!eigenPlek.some(t => elements.isSubtypeOf(type, t))) {
                    naplaatsen.push(node);
                }
                return;
            }
            const normelement = this.normelementVoor(node, mappingsOpWerk, norm);
            if (normelement?.['position']) {
                node.position({...(normelement['position'] as Position)});
                noteer(node);
            } else {
                naplaatsen.push(node);
            }
        });

        //de waarden van een quantity space: de eerste onder de qs, de rest op een rij eronder
        cy.nodes('[type = "quantity_space"]').forEach(qs => {
            const eerste = this.nextQsItem(qs);
            if (eerste?.nonempty()) {
                const qsPos = qs.position();
                eerste.positions({x: qsPos.x, y: qsPos.y + 50});
                this.reorderQs(qs);
                noteer(this.allQsItems(qs));
            }
        });
        //de drie waarden van een afgeleide, onder elkaar zoals bij een nieuwe
        cy.nodes('[type = "derivative"]').forEach(afgeleide => {
            const dpos = afgeleide.position();
            for (const [type, dy] of [['derivative_plus', 50], ['derivative_zero', 80], ['derivative_min', 110]] as const) {
                const waarde = afgeleide.successors(`[type = "${type}"]`).nodes();
                waarde.positions({x: dpos.x, y: dpos.y + dy});
                noteer(waarde);
            }
        });
        //wat geen eigen mapping had: dezelfde afstand tot de parent als vooraf. Een kind van een parent die
        //zelf nog op zijn beurt wacht, gaat achteraan de rij
        let node: NodeSingular | undefined;
        while ((node = naplaatsen.shift())) {
            const parentId = node.data('parentId') as string | undefined;
            if (!parentId || !vooraf[parentId]) {
                console.warn('snapNaarNorm: node zonder mapping en zonder parent blijft staan', node.id());
                continue;
            }
            if (naplaatsen.some(ander => ander.id() === parentId)) {
                naplaatsen.push(node);
                continue;
            }
            const parentPositie = cy.$id(parentId).position();
            node.position({
                x: parentPositie.x - (vooraf[parentId].x - vooraf[node.id()].x),
                y: parentPositie.y - (vooraf[parentId].y - vooraf[node.id()].y)
            });
            noteer(node);
        }
        //exogenen linksboven en de allewaarden-markering rechtsboven hun grootheid
        cy.nodes().forEach(element => {
            const type = element.data('type') as string;
            const dx = elements.isSubtypeOf(type, 'q_exo') ? -60 : (elements.isSubtypeOf(type, 'quantity_allvalues') ? 60 : 0);
            const grootheid = dx ? this.getQuantity(element) : false;
            if (grootheid) {
                const qpos = grootheid.position();
                element.position({x: qpos.x + dx, y: qpos.y - 60});
                noteer(element);
            }
        });
        const posities = verplaatst.map(id => ({id, pos: {...cy.$id(id).position()}}));
        //het hele model staat nu ergens anders, dus het beeld moet mee (vlag naar `bijPositiesGewijzigd`)
        this.zetPosities(posities, true);
        return posities;
    }

    /**
     * Het normelement waar deze node op de plek van moet komen: zijn eigen mapping, of — als zijn type
     * niet gemapt wordt — het enige kind van hetzelfde type onder het normelement van zijn parent.
     * `null` als er geen eenduidig normelement is.
     */
    private normelementVoor(node: NodeSingular, mappingsOpWerk: Record<string, NormmatchMapping>,
                            norm: NormDocument): Record<string, unknown> | null {
        const mapping = mappingsOpWerk[node.id()];
        if (mapping) {
            return mapping.norm;
        }
        const parentId = node.data('parentId') as string | undefined;
        const parent = parentId ? this.getElement(parentId) : null;
        const parentelement = parent?.nonempty() ? this.normelementVoor(parent[0] as NodeSingular, mappingsOpWerk, norm) : null;
        if (!parentelement) {
            return null;
        }
        const type = node.data('type') as string;
        const mogelijk = ((parentelement['childIds'] ?? []) as string[])
            .filter(normId => norm.from.content.elements[normId]?.['type'] === type);
        return mogelijk.length === 1 ? norm.from.content.elements[mogelijk[0]] : null;
    }

    /**
     * Zet het effectieve modelleerniveau op alle nodes (`mln`), zoals een nieuwe node het bij het
     * aanmaken krijgt. Na een wijziging van de modeleigenschappen; de stijl van het canvas leest hem.
     */
    public zetModelleerniveauOpNodes(): void {
        this.cy.nodes().data('mln', this.effectief_modelleerniveau);
    }

    /* ***************** SCHRIJF-API (golf d1, condtag-bron sinds golf a2) ***************** */
    // Condtags: de class-afleiding verhuisde in golf d1 hierheen, de stand waarop hij zich baseert
    // sinds golf a2 naar de `CondtagService`; de samenwerking-, undo-, save- en logplumbing eromheen
    // is met golf a4 naar de actietabel van de samenwerk-baas gegaan.

    /**
     * Alle nodes die in de gegeven conditieset zitten.
     */
    public nodesInCondtag(tag: string): NodeCollection {
        return this.cy.nodes(`[condtag = "${tag}"]`);
    }

    /**
     * Staat er nog niets op het canvas?
     */
    public leeg(): boolean {
        return this.cy.nodes().empty();
    }

    /**
     * Geeft de nodes van een conditieset een nieuwe tagnaam. De zichtbaarheid en de actieve tag zijn
     * van de `CondtagService`; hier gaat alleen de graaf om.
     */
    public hernoemCondtag(oud: string, nieuw: string): void {
        this.nodesInCondtag(oud).data('condtag', nieuw);
    }

    /**
     * Werkt de conditieset-classes op nodes en edges bij, op grond van de zichtbare tags en de
     * actieve condtag uit de `CondtagService`. Autoratief. We matchen op tag en op tag + state:
     * - inconditieset = juiste tag (ongeacht state)
     * - uitconditieset = andere tag
     * - inconditiestate = juiste tag + state
     * - uitconditiestate = niet de juiste tag + state
     * Samen met selectors op `?condtag`/`!condtag` (wel/geen conditioneel item) dekt dit de weergave.
     * @param nodes optionele subset van nodes waarvoor we dit doen (vaak 1 node)
     */
    public updateConditieclasses(nodes?: NodeCollection): void {
        const actieveTags = this.condtagService.actieveTags();
        const condtag = this.condtagService.condtag();
        const cy = this.cy;
        cy.batch(() => {
            const col = nodes || cy.nodes();
            col.removeClass('inconditieset uitconditieset inconditiestate uitconditiestate onderconditie');

            let inset: NodeCollection;
            let instate: NodeCollection;

            if (actieveTags.length) {
                inset = col.filter(actieveTags.map((tag) => `[condtag = "${tag}"]`).join(','));
            } else {
                inset = col.filter('[!condtag]');
            }
            if (condtag) {
                instate = inset.filter(`[condtag = "${condtag.tag}"][condstate = "${condtag.state}"]`);
            } else {
                instate = col.filter('[!condtag]');
            }
            inset.addClass('inconditieset');
            instate.addClass('inconditiestate');

            // en de negaties
            col.not(inset).addClass('uitconditieset');
            col.not(instate).addClass('uitconditiestate');

            // speciaal: values die onder conditievalues vallen krijgen een class
            col.filter('.quantity_value[!condtag],.derivative_value[!condtag]').forEach((v) => {
                if (col.filter(`.inconditieset[?condtag][parentId = "${v.data('parentId')}"]`).nonempty()) {
                    v.addClass('onderconditie');
                }
            });

            // en alle edges
            cy.edges().removeClass('inconditieset uitconditieset inconditiestate uitconditiestate');
            /*
                Een edge is in conditieset als alle connected nodes de juiste conditieset hebben of geen;
                in conditiestate als er een connected node met de juiste conditieset én -state is.
             */
            cy.edges().forEach((edge) => {
                const edgenodes = edge.connectedNodes().map((node) =>
                    node.data('type') === 'edgeHandle'
                        ? cy.getElementById(node.data('handleParent') as string)
                        : node
                );

                let edgeInset = true;
                let edgeInstate = false;

                for (const node of edgenodes) {
                    if (node.data('condtag') && !node.hasClass('inconditieset')) {
                        edgeInset = false;
                    }
                    if (node.data('condtag') && node.hasClass('inconditieset') && node.hasClass('inconditiestate')) {
                        edgeInstate = true;
                    }
                }

                edge.addClass(edgeInset ? 'inconditieset' : 'uitconditieset');
                edge.addClass(edgeInstate ? 'inconditiestate' : 'uitconditiestate');
            });
        });
    }

    /* ***************** SCHRIJF-API (golf d2) ***************** */
    // De docentenfeedback-ballon: de element-data-mutatie en de query erover. De actie eromheen (undo,
    // log, samenwerking, opslaan) is sinds golf a4 de rij `setBallon` van de samenwerk-baas, de weergave
    // sinds golf 4d `dl-ballon` in het modelcanvas.
    // `ballon` krijgt geen regel in `reset()`: het is geen veld van deze class maar element-data, en
    // die gooit het `cy.json({elements: []})` bij het laden al weg.

    /**
     * Zet of verwijdert de docentenfeedback-ballon op een node; `ballon === null` haalt hem weg. Eén
     * methode in plaats van een set/remove-paar, omdat het samenwerkprotocol (`actie: 'setBallon'`
     * met `ballon: null`) en de editor (opslaan met een lege tekst, of wegklikken) al op precies deze
     * null-semantiek uitkomen.
     *
     * Verwijderen gaat met `removeData` en niet met `data('ballon', null)`: dat laatste laat de
     * sleutel in `node.data()` staan, waardoor `"ballon": null` in de opgeslagen modeldata belandt.
     *
     * De parameter is de node en niet een id: de uitvoerder in de baas zoekt hem toch al op, voor zijn
     * guard en de logregel.
     *
     * @param node de node waarop de ballon staat of komt
     * @param ballon de ballon, of `null` om hem weg te halen
     */
    public setBallon(node: NodeSingular, ballon: Ballon | null): void {
        if (ballon) {
            node.data('ballon', {tekst: ballon.tekst, auteur: ballon.auteur});
        } else {
            node.removeData('ballon');
        }
        this._ballonversie.update(versie => versie + 1);
    }

    /**
     * Loopt op bij elke wijziging aan de ballonnen: `setBallon`, en elke hertekening van de graaf (laden,
     * undo, redo, een stand van de leider). `ballonnen()` leest cytoscape en is dus zelf niet reactief;
     * wie hem in een computed leest, leest deze versie erbij.
     */
    public readonly ballonversie: Signal<number> = this._ballonversie.asReadonly();

    /**
     * Alle ballonnen in het model, in cy-volgorde. Query-tegenhanger van `setBallon`, de bron van de
     * ballonlaag in het modelcanvas (die hem samen met `ballonversie` leest).
     *
     * De selector is `[?ballon]` (truthy) en niet `[!ballon]`/`[ballon]` (gedefinieerd): dat laatste
     * matcht óók een node met `ballon: null`, waarna het uitlezen van `.tekst` hieronder zou stuk gaan.
     * `setBallon` maakt dat onbereikbaar door met `removeData` te verwijderen, maar een `"ballon": null`
     * in een ingeladen document komt via `tekenGraaf` rechtstreeks in `node.data` terecht.
     */
    public ballonnen(): BallonWeergave[] {
        return this.cy.nodes('[?ballon]').map((node) => {
            const ballon = node.data('ballon') as Ballon;
            return {nodeId: node.id(), tekst: ballon.tekst, auteur: ballon.auteur};
        });
    }

    /* ***************** SERIALISATIE (golf a2) ***************** */
    // Het opslagformaat zelf (schrijven én lezen, met de interface `ModelDocument`) leeft in
    // `ModelSerialisatie`; hier de publieke ingang en de tekenmelding.

    /**
     * Stelt de modeldata samen: het model als javascript-object in het opslagformaat (geen json).
     */
    public modelData(): ModelDocument {
        return this.serialisatie.schrijf();
    }

    /**
     * Of dit een compleet opgeslagen model is, en dus getekend kan worden; zie
     * `ModelSerialisatie.isVolledigDocument`.
     */
    public isVolledigDocument(doc: ModelDocument | null | undefined): boolean {
        return this.serialisatie.isVolledigDocument(doc);
    }

    /**
     * Bouwt de graaf opnieuw op uit een document (het opslagformaat) en meldt dat daarna aan de
     * luisteraars van `bijGraafGetekend`. De deserialisatie zelf zet alleen de graaf en de
     * documentstate; wat er verder bij een vers document moet
     * gebeuren (de simfeedback wissen, de bouwsuggesties draaien) doen de luisteraars, zodat deze
     * class geen engine kent. De canvas-UI eromheen (selectie, labels, zoom, animatielock) is van
     * `DlModelCanvasComponent.tekenDocument`, de gebruikelijke aanroeper.
     *
     * Het tekenen en het melden gebeuren synchroon, vóór de eerste `await`: als deze methode
     * terugkeert staat de graaf er en zijn de luisteraars aangeroepen. De promise resolvet pas als
     * ook hun asynchrone werk klaar is, zodat een aanroeper daarop kan wachten.
     */
    public async tekenGraaf(doc: Omit<ModelDocument, 'cy'>, aanleiding?: Tekenaanleiding): Promise<void> {
        this.serialisatie.lees(doc);
        this._ballonversie.update(versie => versie + 1); //een vers document heeft zijn eigen ballonnen
        const klaar = this.graafGetekendLuisteraars.map((luisteraar) => luisteraar(aanleiding));
        await Promise.all(klaar);
    }

    /* ***************** HET NORMMODEL (golf a3) ***************** */

    /**
     * Bouwt het `norm`-document op uit een sjabloon en zet het neer: de inhoud van het normmodel,
     * plus de verwachtingen die de normbalk projecteert — per elementtype hoeveel er zouden moeten
     * zijn (`teller`) en per naam hoe vaak hij voorkomt (`namen`). Documentwerk: het gaat over wat
     * er in het model hoort te staan, niet over hoe of wanneer er gematcht wordt.
     *
     * De posities uit de grafische node-data reizen mee in `from.content`, want de normmatch
     * gebruikt ze. Een nulpunt telt onder een eigen naam (`___qszero`), zodat het niet uitmaakt hoe
     * de bouwer van het normmodel hem noemde.
     *
     * @param sjabloonId het model dat als norm dient
     * @param titel de titel van dat model, ter informatie in het document
     * @param doc het opslagformaat van het normmodel
     */
    public bouwNormUitSjabloon(sjabloonId: string, titel: string, doc: ModelDocument): void {
        //de normmatch wil de posities erbij, en die staan in de grafische helft
        for (const normnode of Object.keys(doc.model?.elements || {})) {
            doc.model.elements[normnode]['position'] = doc.nodes[normnode]?.['position'];
        }
        const norm: NormDocument = {
            notationversion: NORM_NOTATIEVERSIE,
            from: {
                id: sjabloonId,
                titel,
                content: doc.model as unknown as NormDocument['from']['content']
            },
            namen: {},
            teller: {
                entity: 0,
                agent: 0,
                assumption: 0,
                configuration: 0,
                attribute: 0,
                quantity: 0,
                quantity_space_point: 0,
                quantity_space_interval: 0,
                ineq: 0,
                correspondence: 0,
                proportionality: 0,
                influence: 0,
                causal: 0, //dubbele telling van de twee hierboven: welke er getoond wordt hangt van het modelleerniveau af
                calc: 0
            }
        };

        const normtypes = Object.keys(norm.teller);
        for (const element of Object.values(doc.model.elements)) {
            if (elements.getDefinition(element.type)?.hasname) {
                const naam = this.normnaamVan(element);
                norm.namen[naam] = (norm.namen[naam] ?? 0) + 1;
            }
            for (const normtype of normtypes) {
                if (elements.isSubtypeOf(element.type, normtype)) {
                    norm.teller[normtype]++; //geen break: een element kan bij meerdere types tellen
                }
            }
        }
        this.norm = norm;
    }

    /**
     * Werkt de notatieversie van het `norm`-document bij naar de huidige. Oude modellen misten de
     * aparte causal-teller en telden waarden en exogenen nog mee; dat wordt hier rechtgezet, in
     * volgorde, zodat een model van elke leeftijd bij de huidige normbalk past.
     */
    public werkNormnotatieBij(): void {
        const norm = this.norm;
        if (!norm) {
            return;
        }
        const versie = norm.notationversion || 1;
        if (versie < 2 && norm.teller) {
            //de causals kregen een eigen teller, als som van de twee soorten
            norm.teller['causal'] = (norm.teller['proportionality'] || 0) + (norm.teller['influence'] || 0);
            norm.notationversion = 2;
        }
        if (versie < 3 && norm.teller) {
            //waarden en exogenen tellen niet meer mee
            delete norm.teller['value'];
            delete norm.teller['q_exo'];
            delete norm.teller['quantity_allvalues'];
            norm.notationversion = 3;
        }
        if (versie < 5 && norm.teller) {
            norm.notationversion = NORM_NOTATIEVERSIE;
        }
        if (norm.notationversion > NORM_NOTATIEVERSIE) {
            console.warn('De notatieversie van de norm is hoger dan wij kennen. Uit een developmentbranch?');
        }
    }

    /**
     * De naam waaronder een element in de normtelling meetelt: getrimd, met enkele spaties. Een
     * nulpunt krijgt een eigen tag, die vertaald wordt waar hij getoond wordt.
     */
    private normnaamVan(element: ModelElementData): string {
        if (element.type === 'quantity_space_point' && element['isZero']) {
            return '___qszero';
        }
        return String(element['nodename'] ?? '').trim().replace(/\s{2,}/g, ' ');
    }

    /* ***************** BESCHRIJVING VOOR DE ACTIELOG (golf a3) ***************** */

    /**
     * De route naar een node in begrijpelijke taal: de beschrijvingen van zijn parents, met `|`
     * ertussen, en die van de node zelf achteraan. Bij een element van een quantity space of van
     * een afgeleide springt de route direct naar de grootheid, want de parents daartussen zijn de
     * bovenliggende elementen en zeggen de lezer niets.
     *
     * Dit is een graafquery en geen opslagzaak: de actielog gebruikt hem, maar hij beschrijft het
     * document. Geeft `"?"` als het element niet (meer) in de graaf staat.
     */
    public route(node: NodeSingular | CollectionReturnValue | false | null | undefined): string {
        if (!node || !(node as CollectionReturnValue).length) {
            //geen element (meer) in de graaf: een lege collectie uit getElement, of false uit getNodeUp
            return '?';
        }
        const element = node as NodeSingular;
        const nodetype = element.data('type') as string;
        let routestr = '';
        if (elements.isSubtypeOf(nodetype, 'quantity_space_element') || elements.isSubtypeOf(nodetype, 'derivative_element')) {
            routestr = this.route(this.getNodeUp(element, ['quantity'])) + '|';
        } else if (element.data('parentId')) {
            routestr = this.route(this.getElement(element.data('parentId') as string)) + '|';
        }
        return routestr + this.beschrijf(element);
    }

    /**
     * Een leesbare beschrijving van één node, voor de actielog en de rapportage: type, id en naam.
     * Een nulpunt heet `zero`, ongeacht hoe de gebruiker hem noemde.
     */
    public beschrijf(node: NodeSingular): string {
        const type = node.data('type') as string | undefined;
        const isZero = type === 'quantity_space_point' && !!node.data('isZero');
        const naam = isZero ? 'zero' : (node.data('nodename') as string | undefined) || '_';
        return `${type || '_'}:${node.id()}:${naam}`;
    }

    /**
     * Registreert een luisteraar voor het moment dat de graaf uit een document is opgebouwd. De
     * luisteraars lopen synchroon, in volgorde van registratie, direct na de deserialisatie — dus
     * vóórdat de aanroeper van `tekenGraaf` verdergaat. Dat is bewust: een replaystap past ná het
     * tekenen de simulatiestatus toe, en het wissen van de simfeedback moet daar vóór zitten. Een
     * luisteraar mag een promise teruggeven; `tekenGraaf` wacht daar in zijn eigen promise op.
     */
    public bijGraafGetekend(luisteraar: (aanleiding?: Tekenaanleiding) => void | Promise<void>): void {
        this.graafGetekendLuisteraars.push(luisteraar);
    }

    /**
     * De actieve model-`cy`. Gooit als er (nog) geen canvas is; in de praktijk gebruiken de aanroepers
     * de query-API pas als het modelcanvas bestaat.
     */
    private get cy(): Core {
        const cy = this.modelcanvas.getCy();
        if (!cy) {
            throw new Error('DlModel: geen actief modelcanvas beschikbaar');
        }
        return cy;
    }

    /**
     * Voegt een afgeleide (`derivative`) toe aan een quantity, inclusief zijn drie
     * waarde-elementen (plus/nul/min, als een soort linked list aan elkaar gekoppeld). Geeft een
     * lege array als de quantity al een afgeleide heeft.
     */
    private addDerivative(
        qNode: NodeSingular, position: Position, condtag: string | null, condstate: string | null
    ): NodeSingular[] {
        if (this.outElements(qNode, '[type = "derivative"]').nonempty()) {
            return [];
        }
        const dNode = this.addCyNode({
            data: {type: 'derivative', automoveorder: 999, condtag, condstate},
            position
        }, qNode, false);
        dNode.data('automovegroup', 'dgroup-' + dNode.id());
        const group = dNode.data('automovegroup') as string;

        let x = position.x;
        let y = position.y + 50;
        const plusNode = this.addCyNode({
            data: {type: 'derivative_plus', derivative: dNode.id(), automovegroup: group, condtag, condstate},
            position: {x, y}
        }, dNode, false);

        y += 30;
        const zeroNode = this.addCyNode({
            data: {type: 'derivative_zero', derivative: dNode.id(), automovegroup: group, condtag, condstate},
            position: {x, y}
        }, plusNode, false);

        y += 30;
        const minNode = this.addCyNode({
            data: {type: 'derivative_min', derivative: dNode.id(), automovegroup: group, condtag, condstate},
            position: {x, y}
        }, zeroNode, false);

        return [dNode, plusNode, zeroNode, minNode];
    }

    /**
     * Zoekt vanaf `argnode` omhoog (via `parentId`) naar een node waarvan het type in `nodetypes`
     * staat. Geeft die node terug, of `false`.
     */
    private getNodeUp(argnode: NodeSingular | string, nodetypes: string[]): NodeSingular | false {
        const node = typeof argnode !== 'object' ? this.cy.getElementById(argnode) as unknown as NodeSingular : argnode;
        if (nodetypes.indexOf(node.data('type')) !== -1) {
            return node;
        }
        const parentId = node.data('parentId') as string | undefined;
        return parentId ? this.getNodeUp(parentId, nodetypes) : false;
    }

    /**
     * Alle elementen onder de gegeven qs-node. Publiek sinds golf a4: de waarde- en
     * nulpunt-uitvoerders lopen deze lijst af.
     */
    public allQsItems(qsNode: NodeCollection): CollectionReturnValue {
        return this.cy.$("[qspace = '" + (qsNode as unknown as NodeSingular).id() + "']");
    }

    /**
     * De quantity-space waar dit item (een punt, interval of waarde) onder hangt.
     */
    public qsNodeVoorItem(qsItem: NodeSingular): NodeSingular | false {
        return this.getNodeUp(qsItem, ['quantity_space']);
    }

    /**
     * Of een quantity-space-element weg mag: alleen de bovenste of de onderste, en er moet er één
     * overblijven.
     */
    public magQsElementWeg(node: NodeSingular): boolean {
        const vorige = this.prevQsItem(node);
        const volgende = this.nextQsItem(node);
        //er moet er precies één ontbreken
        return !!((vorige && !volgende) || (volgende && !vorige));
    }

    /**
     * Alle qs-items voor de qNode; lege collectie als er geen quantityspace is.
     */
    private getFullQs(qNode: NodeSingular | string): CollectionReturnValue {
        const qsNode = this.getQSNode(qNode);
        if (!qsNode) {
            return this.cy.collection();
        }
        return this.allQsItems(qsNode);
    }

    /**
     * Cast-helper voor de (via `cytoscape-extensies` geregistreerde, niet-getypeerde) `outElements`.
     * Publiek sinds golf a4: de uitvoerders van de modelacties zoeken hiermee door de graaf, en de
     * cast hoort op één plek te staan.
     */
    public outElements(el: NodeSingular | NodeCollection, selector?: string | string[]): NodeCollection {
        return (el as unknown as {outElements: (s?: string | string[]) => NodeCollection}).outElements(selector);
    }

    /**
     * Cast-helper voor de (via `cytoscape-extensies` geregistreerde, niet-getypeerde) `inElements`.
     */
    private inElements(el: NodeSingular | NodeCollection, selector?: string | string[]): NodeCollection {
        return (el as unknown as {inElements: (s?: string | string[]) => NodeCollection}).inElements(selector);
    }

    /**
     * Regel de juiste handle voor een edge naar/van een node: dat is de node zelf, of een speciale
     * edgeHandle-node als die voor dit relatietype in de elementdefinitie staat.
     */
    private getEdgeHandle(parentNode: NodeSingular, handleType: string): NodeSingular {
        const edgeHandles = elements.getDefinition(parentNode.data('type') as string).edgeHandles;
        if (!(edgeHandles && edgeHandles[handleType] && edgeHandles[handleType].length)) {
            return parentNode;
        }
        const nodepos = edgeHandles[handleType][0];
        const parentPos = parentNode.position();
        let classes = 'edgeHandle'; // NIET class model, dus niet opgeslagen
        if (parentNode.hasClass('hide_in_simulate')) {
            classes += ' hide_in_simulate';
        }
        const edgeNode = this.cy.add({
            group: 'nodes',
            classes,
            selectable: false,
            data: {
                type: 'edgeHandle',
                handleParent: parentNode.id(),
                handleType,
                id: this.genereerId('eh'),
                freeMove: false // als true, dan is deze vrij te verplaatsen. Vooral om de juiste offset te vinden
            },
            position: {
                x: parentPos.x + nodepos.x,
                y: parentPos.y + nodepos.y
            },
            scratch: {
                offsets: edgeHandles[handleType],
                currentOffset: 0
            }
        }) as unknown as NodeSingular;
        if (edgeNode.data('freeMove')) {
            edgeNode.on('drag', () => {});
        }
        return edgeNode;
    }

    /**
     * Geef gegeven een sourcetype/targettype/relatietype de juiste edge-styleclasses terug.
     * Classes die worden toegevoegd: `edge_from_<sourcetype>`, `edge_to_<targettype>`,
     * `edge_type_<relatietype>` (standaard `normal`), plus voor elke parent in de isa-hiërarchie
     * `edge_from_isa_<parent>`/`edge_to_isa_<parent>`.
     */
    private edgeClasses(sourceType: string, targetType: string, reltype: string, stateclass: string): string {
        const classes = ['edge_from_' + sourceType, 'edge_to_' + targetType, stateclass];
        for (const parent of elements.alleUps(sourceType)) {
            classes.push('edge_from_isa_' + parent);
        }
        for (const parent of elements.alleUps(targetType)) {
            classes.push('edge_to_isa_' + parent);
        }
        classes.push('edge_type_' + (reltype || 'normal'));
        return classes.join(' ');
    }

    /**
     * Genereert een uniek element-id. Zelfde algoritme als `DlCytoCanvasBaseDirective.getId`
     * (prefix + timestamp, met tellersuffix bij botsing); hier gedupliceerd omdat `DlModel` niet van
     * die (Angular-component-gebonden) directive afhangt.
     */
    private genereerId(prefix: string): string {
        let id = prefix + Date.now();
        if (this.cy.hasElementWithId(id)) {
            let teller = 2;
            while (this.cy.hasElementWithId(id + teller)) {
                teller++;
            }
            id = id + teller;
        }
        return id;
    }
}
